import React, { useState } from "react";
import axios from "axios";
import { makeStyles } from 'tss-react/mui';
import { Box, Button, CircularProgress, Grid, IconButton, Paper, Tooltip, TextField, Typography } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";

import { i18n } from "../../translate/i18n";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  intro: {
    color: theme.palette.text.secondary,
    maxWidth: 720,
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
    fontSize: 13.5,
  },
  sectionTitle: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
    fontWeight: 700,
  },
  noteBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.action.hover,
    fontSize: 12.5,
    lineHeight: 1.5,
    maxWidth: 720,
    "& ul": { margin: 0, paddingLeft: 18 },
    "& li": { marginBottom: 2 },
  },
  referenceCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  codeRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  codeLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    minWidth: 52,
    flexShrink: 0,
  },
  code: {
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: 12,
    background: theme.palette.mode === "dark" ? "#0b0e14" : "#f4f6fa",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(0.5, 1),
    flex: 1,
    overflowX: "auto",
    whiteSpace: "pre",
    ...theme.scrollbarStyles,
  },
  curlWrap: {
    marginTop: theme.spacing(1),
    position: "relative",
  },
  curlBlock: {
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: 11.5,
    lineHeight: 1.5,
    background: theme.palette.mode === "dark" ? "#0b0e14" : "#f4f6fa",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    paddingRight: theme.spacing(4.5),
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: 0,
    ...theme.scrollbarStyles,
  },
  copyButton: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  testCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  testTitle: {
    fontWeight: 700,
    fontSize: 13,
    marginBottom: theme.spacing(1),
  },
  textRight: {
    textAlign: "right",
  },
}));

const CodeLine = ({ classes, label, value }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className={classes.codeRow}>
      <span className={classes.codeLabel}>{label}</span>
      <span className={classes.code}>{value}</span>
      <Tooltip title="Copiar">
        <IconButton size="small" onClick={copy}>
          <ContentCopyOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const CurlExample = ({ classes, command }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className={classes.curlWrap}>
      <pre className={classes.curlBlock}>{command}</pre>
      <Tooltip title="Copiar">
        <IconButton size="small" className={classes.copyButton} onClick={copy}>
          <ContentCopyOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const MessagesAPI = () => {
  const { classes } = useStyles();

  const [formMessageTextData,] = useState({ token: '', number: '', body: '' })
  const [formMessageMediaData,] = useState({ token: '', number: '', medias: '' })
  const [file, setFile] = useState({})

  const endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/messages/send`;

  const handleSendTextMessage = async (values) => {
    const { number, body } = values;
    const data = { number, body };
    var options = {
      method: 'POST',
      url: endpoint,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${values.token}`
      },
      data
    };

    axios.request(options).then(function (response) {
      toast.success('Mensaje enviado con éxito');
    }).catch(function (error) {
      toastError(error);
    });
  }

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append('number', values.number);
      data.append('body', firstFile.name);
      data.append('medias', firstFile);
      var options = {
        method: 'POST',
        url: endpoint,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${values.token}`
        },
        data
      };

      axios.request(options).then(function (response) {
        toast.success('Mensaje enviado con éxito');
      }).catch(function (error) {
        toastError(error);
      });
    } catch (err) {
      toastError(err);
    }
  }

  const renderFormMessageText = () => {
    return (
      <Formik
        initialValues={formMessageTextData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendTextMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
          }, 400);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.token")}
                  name="token"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.number")}
                  name="number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="593999999999"
                  required
                />
              </Grid>
              <Grid size={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid className={classes.textRight} size={12}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="small"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Enviar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }

  const renderFormMessageMedia = () => {
    return (
      <Formik
        initialValues={formMessageMediaData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendMediaMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
            document.getElementById('medias').files = null
            document.getElementById('medias').value = null
          }, 400);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.token")}
                  name="token"
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.number")}
                  name="number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="593999999999"
                  required
                />
              </Grid>
              <Grid size={12}>
                <input type="file" name="medias" id="medias" required onChange={(e) => setFile(e.target.files)} />
              </Grid>
              <Grid className={classes.textRight} size={12}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="small"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Enviar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }

  const curlText = `curl -X POST "${endpoint}" \\
  -H "Authorization: Bearer TU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"number": "593999999999", "body": "Hola, este es un mensaje de prueba"}'`;

  const curlMedia = `curl -X POST "${endpoint}" \\
  -H "Authorization: Bearer TU_TOKEN" \\
  -F "number=593999999999" \\
  -F "medias=@/ruta/al/archivo.jpg"`;

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <Typography variant="h5">
        Documentación para el envío de mensajes
      </Typography>
      <Typography className={classes.intro}>
        Enviá mensajes de WhatsApp desde tu propio sistema con una sola
        llamada HTTP, autenticada con el token de la conexión.
      </Typography>

      <Typography variant="h6" color="primary" className={classes.sectionTitle}>
        Antes de empezar
      </Typography>
      <Box className={classes.noteBox}>
        <ul>
          <li>
            Necesitás el <b>token</b> de la conexión que va a enviar los mensajes.
            Se genera desde <b>Conexiones → editar la conexión → campo "Token"</b>.
          </li>
          <li>
            El token viaja siempre en el header <code>Authorization</code>, con
            el formato <code>Bearer TU_TOKEN</code> — no en un header custom.
          </li>
          <li>
            El número va sin espacios, guiones ni signos: código de país
            seguido del número, todo junto (ej. <code>593999999999</code> para
            Ecuador). Sin el <code>+</code> inicial.
          </li>
        </ul>
      </Box>

      <Typography variant="h6" color="primary" className={classes.sectionTitle}>
        1. Mensajes de texto
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box className={classes.referenceCard}>
            <CodeLine classes={classes} label="POST" value={endpoint} />
            <CurlExample classes={classes} command={curlText} />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box className={classes.testCard}>
            <Typography className={classes.testTitle}>Probar ahora</Typography>
            {renderFormMessageText()}
          </Box>
        </Grid>
      </Grid>

      <Typography variant="h6" color="primary" className={classes.sectionTitle}>
        2. Mensajes multimedia
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box className={classes.referenceCard}>
            <CodeLine classes={classes} label="POST" value={endpoint} />
            <CurlExample classes={classes} command={curlMedia} />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box className={classes.testCard}>
            <Typography className={classes.testTitle}>Probar ahora</Typography>
            {renderFormMessageMedia()}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MessagesAPI;
